import React, { useState, useEffect } from 'react';
import { bool, func, string, array, object } from 'prop-types';
import useTranslation from '@airasia-common/libraries/i18n/useTranslation';
import { KingBed, BeachAccess, SmokeFree } from '@material-ui/icons';
import { specialRequestType } from '@airasia-common/libraries/constants/specialRequestType';
import theme from '@airasia-common/libraries/theming/theme';
import styled from 'styled-components';
import Wrapper from '../Wrapper';
import Button from '../Button';
import Row from '../Container/Row';
import Column from '../Container/Column';
import { SubBodyText, BodyText, H3 } from '../Text/Heading';
import CheckboxesGroup from '../CheckboxesGroup';
import RadioGroupWithTitle from '../Radio/RadioGroupWithTitle';
import ResponsiveModal from '../Modal/ResponsiveModal';
import { PickerSelect } from '../PickerSelect';
import { Clock } from '../Amenity/MaterialIcon';

const MAX_MODAL_WIDTH = 600;
const iconStyles = { color: theme.colors.greyDark };
const specialRequestIcons = {
  [specialRequestType.bed_prefs]: <KingBed style={iconStyles} />,
  [specialRequestType.smoking_prefs]: <SmokeFree style={iconStyles} />,
  [specialRequestType.extra_prefs]: <BeachAccess style={iconStyles} />,
  [specialRequestType.time_prefs]: <Clock style={iconStyles}></Clock>,
};

const Title = styled(SubBodyText)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.darkBlack};
`;

const SpecialRequestModal = ({
  defaultValue,
  specialRequests,
  onSave,
  isClickTrack,
  clickTrack,
  fillTrack,
  theme,
  move,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [formValue, setForm] = useState([]);
  const [selectedPreferTime, setSelectedPreferTime] = useState();
  const translate = useTranslation();

  const handleOnChange = (id, item) => {
    const newForm = [...formValue];
    const specialRequestIndex = newForm.findIndex(item => item.id === id);
    if (!Array.isArray(item)) {
      newForm[specialRequestIndex] = {
        id,
        value: [item.value],
      };
    } else {
      newForm[specialRequestIndex] = { id, value: item };
    }
    setForm(newForm);
  };

  const selectPreferTime = (value, item) => {
    console.log('@@@ select picker', value, item);
    setSelectedPreferTime(value);
  };

  useEffect(() => {
    if (isOpen) {
      if (defaultValue.length > 0) {
        setForm([...defaultValue]);
      } else if (specialRequests) {
        const initialForm = specialRequests.map(item => ({
          id: item.id,
          value: [],
        }));
        setForm(initialForm);
      }
    }
  }, [specialRequests, defaultValue, isOpen]);

  const isHaveValue = defaultValue.some(request => request.value.length > 0);

  return (
    <>
      <Wrapper>
        <Row alignItems="center">
          <H3 color="black" fontSize={['14px', '20px']}>
            {translate('common.specialRequests')}
          </H3>
          <BodyText
            color="greyDark"
            fontWeight="normal"
            ml="5px"
            fontSize={['14px', '16px']}
          >
            ({translate('common.optional')})
          </BodyText>
        </Row>
        <Row>
          <SubBodyText fontWeight="normal" color="greyDark">
            {translate('specialRequests.note')}
          </SubBodyText>
        </Row>
        <Row mt={3}>
          <BodyText
            color={move ? 'tertiaryRed' : 'secondary'}
            cursor="pointer"
            onClick={() => {
              isClickTrack && clickTrack();
              setOpen(true);
            }}
          >
            {isHaveValue
              ? translate('specialRequests.edit')
              : translate('specialRequests.add')}
          </BodyText>
        </Row>
      </Wrapper>
      <ResponsiveModal
        width={[0.8, MAX_MODAL_WIDTH]}
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        modalStyle={{
          top: ['16px', '50%'],
          width: ['auto', '456px'],
          bottom: ['16px', 'auto'],
          left: '50%',
          right: 'auto',

          style: {
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 101,
            },
          },
        }}
      >
        <Column p={[24, 32]}>
          <H3 color="black" fontSize={'20px'}>
            {translate('specialRequests.title')}
          </H3>
          <Wrapper>
            {specialRequests.map(request => {
              const selectedRequest = formValue.find(
                value => value.id === request.id
              );
              const selectedOptions = selectedRequest
                ? selectedRequest.value
                : [];
              let selectionComponent = null;
              const translationRequestTitle = `specialRequests.${request.id}`;
              const optionsWithTranslation = request.options.map(option => {
                return {
                  id: option.id,
                  value: translate(`${translationRequestTitle}.${option.id}`),
                };
              });
              const isVertical = optionsWithTranslation.length >= 3;
              //exclusive  == true is single option
              if (request.exclusive) {
                selectionComponent = (
                  <RadioGroupWithTitle
                    theme={theme}
                    move={move}
                    options={optionsWithTranslation}
                    displayTextProp="value"
                    value={selectedOptions[0]}
                    onChange={value => handleOnChange(request.id, value)}
                    title={translate(translationRequestTitle)}
                    iconPrefixTitle={specialRequestIcons[request.id]}
                    vertical={isVertical}
                  />
                );
              } else if (request.preferedTimes) {
                selectionComponent = (
                  <>
                    <Wrapper pb={3} display="flex" flexDirection="row">
                      {specialRequestIcons[request.id]}
                      <Title pl={10}>
                        {translate(`${translationRequestTitle}`)}
                      </Title>
                    </Wrapper>
                    <PickerSelect
                      label={'Check in time'}
                      placeholder={'Select an option'}
                      defaultValue={''}
                      items={request.options}
                      value={selectedPreferTime}
                      onChange={selectPreferTime}
                    />
                  </>
                );
              } else {
                selectionComponent = (
                  <CheckboxesGroup
                    isMoveEnabled={move}
                    options={optionsWithTranslation}
                    displayTextProp="value"
                    selectedOptions={selectedOptions}
                    onChange={value => handleOnChange(request.id, value)}
                    title={translate(translationRequestTitle)}
                    iconPrefixTitle={specialRequestIcons[request.id]}
                  />
                );
              }
              return (
                <Wrapper key={request.id} mt={5}>
                  {selectionComponent}
                </Wrapper>
              );
            })}
          </Wrapper>
          <Row
            mt={[0, '40px']}
            position={['absolute', 'inherit']}
            left="24px"
            bottom="24px"
            right="24px"
            flexDirection={['column-reverse', 'row']}
          >
            <Wrapper pr={[0, 3]} width="100%">
              <Button
                block
                big
                secondary
                height="48px"
                onClick={() => {
                  setOpen(false);
                }}
                move
                border={move && '#023C40'}
                borderRadius={move && '32px'}
                hovercolor={move && '#E8F2EC'}
              >
                {translate('specialRequests.cancel')}
              </Button>
            </Wrapper>
            <Wrapper pl={[0, 3]} mb={[3, 0]} width="100%">
              <Button
                block
                big
                onClick={() => {
                  onSave(formValue);
                  isClickTrack && fillTrack(formValue);
                  setOpen(false);
                }}
                border={move && '#023C40'}
                borderRadius={move && '32px'}
                hovercolor={move && '#12C25A'}
                backgroundColor={move && '#0CE865'}
              >
                {translate('specialRequests.save')}
              </Button>
            </Wrapper>
          </Row>
        </Column>
      </ResponsiveModal>
    </>
  );
};

SpecialRequestModal.propTypes = {
  title: string,
  isOpen: bool,
  onClose: func,
  specialRequests: array,
  onSave: func,
  defaultValue: object,
  isClickTrack: bool,
  clickTrack: func,
  fillTrack: func,
  theme: string,
  move: bool,
};

export default SpecialRequestModal;
